import { useNavigate } from 'react-router-dom'
import { Upload, TrendingUp } from 'lucide-react'
import { useJobs } from '../hooks/useJobs'
import { fmtEUR, fmtNum, fmtPct, fmtDateTime } from '../utils/format'
import { StatCard, StatusBadge, SectionHeader, Empty } from '../components/UI'

export default function Dashboard() {
  const { jobs, isLoading, isError, stats } = useJobs()
  const navigate = useNavigate()
  const doneJobs  = jobs.filter(j => j.status === 'done')
  const recRate   = stats.total_spend > 0 ? (stats.total_vat / stats.total_spend) * 100 : 0

  return (
    <div>

      {/* Backend offline banner */}
      {isError && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
          padding: '12px 16px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <div>
            <p style={{ fontWeight: 600, color: '#b91c1c', fontSize: 13.5, margin: 0 }}>
              Cannot reach backend
            </p>
            <p style={{ color: '#b91c1c', fontSize: 12.5, margin: '2px 0 0', opacity: .8 }}>
              Make sure <code style={{ background: '#fee2e2', padding: '1px 5px', borderRadius: 4 }}>./start.sh</code> is running and the server is on port 8000.
            </p>
          </div>
        </div>
      )}
      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-1)', marginBottom: 4 }}>
          VAT Recovery Dashboard
        </h1>
        <p style={{ fontSize: 13.5, color: 'var(--text-2)' }}>
          Aggregate results across all processed files
        </p>
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: 16, marginBottom: 28 }}>
        <StatCard
          label="Total VAT Recoverable"
          value={fmtEUR(stats.total_vat)}
          sub={`Across ${stats.files_done} file${stats.files_done !== 1 ? 's' : ''}`}
          accent="#0096c7"
          highlight={stats.total_vat > 0}
        />
        <StatCard
          label="Total Spend Analysed"
          value={fmtEUR(stats.total_spend, 0)}
          sub={`${fmtNum(stats.total_txns)} transactions`}
          accent="#7c3aed"
        />
        <StatCard
          label="Files Processed"
          value={String(stats.files_done)}
          sub={`${stats.files_active} processing · ${stats.files_error} errors`}
          accent="#ea580c"
        />
        <StatCard
          label="Recovery Rate"
          value={stats.total_spend > 0 ? fmtPct(recRate, 2) : '—'}
          sub="VAT ÷ Total spend"
          accent="#22c55e"
        />
      </div>

      {/* Empty state */}
      {!isLoading && jobs.length === 0 && (
        <div className="card">
          <Empty
            icon="📊"
            title="No files processed yet"
            sub="Upload your hotel & travel expense files to start recovering VAT"
            action={
              <button className="btn btn-primary" style={{ margin: '0 auto' }} onClick={() => navigate('/upload')}>
                <Upload size={14} /> Upload First File
              </button>
            }
          />
        </div>
      )}

      {/* Files table */}
      {doneJobs.length > 0 && (
        <div className="card">
          <div className="card-header">
            <div>
              <p className="section-title">Processed Files</p>
              <p className="section-sub">Click View to see full breakdown</p>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/upload')}>
              <Upload size={12} /> Add More
            </button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead><tr>
                <th>File</th>
                <th style={{ textAlign: 'right' }}>Transactions</th>
                <th style={{ textAlign: 'right' }}>Total Spend</th>
                <th style={{ textAlign: 'right' }}>VAT Potential</th>
                <th>Completed</th>
                <th>Status</th>
                <th></th>
              </tr></thead>
              <tbody>
                {doneJobs.map(j => (
                  <tr key={j.id}>
                    <td style={{ fontWeight: 500, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {j.original_filename}
                    </td>
                    <td className="mono" style={{ textAlign: 'right' }}>{fmtNum(j.total_transactions)}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{fmtEUR(j.total_spend_eur, 0)}</td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 700, color: '#15803d' }}>
                      {fmtEUR(j.total_vat_eur)}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {j.completed_at ? fmtDateTime(j.completed_at) : '—'}
                    </td>
                    <td><StatusBadge status={j.status} /></td>
                    <td>
                      <button className="btn btn-outline btn-sm" onClick={() => navigate(`/queue/${j.id}`)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Active jobs banner */}
      {stats.files_active > 0 && (
        <div style={{
          marginTop: 16, padding: '14px 20px', borderRadius: 12,
          background: '#eff6ff', border: '1px solid #bfdbfe',
          display: 'flex', alignItems: 'center', gap: 12
        }}>
          <span className="dot-pulse" style={{ color: '#3b82f6' }} />
          <div style={{ flex: 1 }}>
            <p style={{ fontWeight: 600, color: '#1d4ed8', fontSize: 13 }}>
              {stats.files_active} file{stats.files_active > 1 ? 's' : ''} processing
            </p>
            <p style={{ fontSize: 12, color: '#3b82f6', marginTop: 1 }}>
              Results appear automatically — you can keep working
            </p>
          </div>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/queue')}>View Queue</button>
        </div>
      )}
    </div>
  )
}